import { Text, IconConfigureLine, Button, Drilldown, Pill } from '@smarterservices/smarter-ui';
import { capitalizeFirstLetter } from '@instructure/ui-utils';
import { randomId } from '../../../../helpers/utilities';
import { useCallback, useEffect, useState } from 'react';
import { getStore, usePlayerStore } from '../../../../stores/StoreContext';
import CONSTANTS from '../../../../config/constants';
import NavigationalMenuItem from '@ui/shared/components/Common/NavigationalMenuItem';
const AnnotationOptions = () => {
  const PlayerStore = getStore('playerStore');

  {
    /* Listen to any changes in the stores... */
  }
  const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
  const displayMarkers = usePlayerStore((state) => state.displayMarkers);
  const displayVideoLabels = usePlayerStore((state) => state.displayVideoLabels);
  const smartSpeedEnabled = usePlayerStore((state) => state.smartSpeedEnabled);
  const smartSpeedName = usePlayerStore((state) => state.smartSpeedName);

  const [markerBulkUpdateLabel, setMarkerBulkUpdateLabel] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    const totalOptions = PlayerStore.markers.length;
    let selectedOptions = 0;
    // see if all the markers are selected, if so we will want to toggle the enable/disable all title.
    for (const marker of PlayerStore.markers) {
      if (displayMarkers.includes(marker.id)) {
        selectedOptions = selectedOptions + 1;
      }
    }
    if (!bulkUpdating) {
      if (totalOptions === selectedOptions) {
        setMarkerBulkUpdateLabel('Disable All');
      } else {
        setMarkerBulkUpdateLabel('Enable All');
      }
    }
  }, [displayMarkers]);

  const bulkUpdateSelectedAnnotations = async (event) => {
    setBulkUpdating(true);
    if (PlayerStore.markers.length === displayMarkers.length) {
      // disable all...
      setMarkerBulkUpdateLabel('Disabling...');
      for (const marker of PlayerStore.markers) {
        if (displayMarkers.includes(marker.id) && marker.id !== 'flags') {
          await PlayerStore.toggleMarker(marker.id);
        }
      }
      setMarkerBulkUpdateLabel('Enable All');
    } else {
      // enable all...
      setMarkerBulkUpdateLabel('Enabling...');
      for (const marker of PlayerStore.markers) {
        if (!displayMarkers.includes(marker.id)) {
          await PlayerStore.toggleMarker(marker.id);
        }
      }
      setMarkerBulkUpdateLabel('Disable All');
    }
    setBulkUpdating(false);
  };

  const handleAnnotationSelect = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  const handleSpeedSelect = useCallback((value) => {
    PlayerStore.setPlaybackSpeed(value);
  });

  const handleVideoLabelsSelect = useCallback((value) => {
    PlayerStore.setDisplayVideoLabels(value);
  });

  const renderAnnotationOptionGroup = () => {
    const options = [];

    for (const marker of PlayerStore.markers) {
      // don't allow them to disable the flags...
      if (marker.id === 'flags') {
        continue;
      }

      options.push(
        <Drilldown.Option
          onSelect={handleAnnotationSelect}
          id={marker.id}
          selected={displayMarkers.includes(marker.id)}
          key={`marker_${marker.id}`}
          value={marker.id}
          onOptionClick={async () => await PlayerStore.toggleMarker(marker.id)}
        >
          <NavigationalMenuItem
            shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.[marker.label.toUpperCase()]?.KEY} >
            {marker.label}
          </NavigationalMenuItem>
        </Drilldown.Option>
      );
    }

    return (
      <Drilldown.Group
        id={`annotations_group`}
        selectableType="multiple"
        //defaultSelected={[selectedState]}
        onSelect={handleAnnotationSelect}
      >
        {options}
      </Drilldown.Group>
    );
  };

  const renderSpeedOptionGroup = () => {
    const options = [];
    let _playbackSpeed = playbackSpeed;
    if (smartSpeedEnabled) {
      _playbackSpeed = '100.0';
    }
    for (const speed of PlayerStore.speedOptions) {
      options.push(
        <Drilldown.Option
          id={`${speed.value}`}
          selected={_playbackSpeed === PlayerStore.formatSpeedValue(speed.value)}
          key={`marker_${randomId()}`}
          value={speed.value}
          onOptionClick={() => handleSpeedSelect(speed.value)}
        >
          <NavigationalMenuItem
            shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.[speed.key.toUpperCase()]?.KEY} >
            {speed.label}
          </NavigationalMenuItem>
        </Drilldown.Option>
      );
    }

    return (
      <Drilldown.Group id={`speed_group`} selectableType="single">
        {options}
      </Drilldown.Group>
    );
  };

  const renderVideoLabelsOptionGroup = () => {
    const options = [];


    options.push(
      <Drilldown.Option
        id="show_video_labels"
        key={`marker_${randomId()}`}
        selected={displayVideoLabels === "show"}
        value="show"
        onOptionClick={() => handleVideoLabelsSelect("show")}
      >
        <NavigationalMenuItem
          shortcut={CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.VIDEO_LABELS.SHOW.KEY} >Show</NavigationalMenuItem>
      </Drilldown.Option>
    );
    options.push(
      <Drilldown.Option
        id="hide_video_labels"
        key={`marker_${randomId()}`}
        selected={displayVideoLabels === "hide"}
        value="hide"
        onOptionClick={() => handleVideoLabelsSelect("hide")}
      >
        <NavigationalMenuItem shortcut={CONSTANTS.KEYBOARD_SHORTCUTS.VIEW.OPTIONS.VIDEO_LABELS.HIDE.KEY} >Hide</NavigationalMenuItem>
      </Drilldown.Option>
    );


    return (
      <Drilldown.Group id={`video_labels_group`} selectableType="single">
        {options}
      </Drilldown.Group>
    );
  };

  const renderSelected = (props, value) => {
    return (
      <Pill size="small">
        {value}
      </Pill>
    );
  };

  return (
    <Drilldown
      rootPageId="videoSettings"
      width="18.5rem"
      maxHeight="26.5rem"
      shouldHideOnSelect={false}
      trigger={
        <Button
          size="small"
          margin="none none none x-small"
          screenReaderLabel="View annotation options"
        >
          <IconConfigureLine /> <Text size="small"></Text>
        </Button>
      }
      onToggle={(_event, { shown, goToPage }) => {
        shown && goToPage('videoSettings');
      }}
    >
      <Drilldown.Page
        id="videoSettings"
        renderTitle="Video Settings"
        withoutHeaderSeparator
      >
        <Drilldown.Option
          id="Annotations"
          subPageId="annotations"
          renderLabelInfo={(props) => {
            // since flags are always displayed, if that is the only one don't show any count...
            if (displayMarkers?.length === 1) {
              return null;
            }
            return renderSelected(props, `${displayMarkers?.length - 1} selected`)
          }}
        >
          Markers
        </Drilldown.Option>
        <Drilldown.Option
          id="speed"
          subPageId="speed"
          renderLabelInfo={(props) => {
            if (smartSpeedEnabled) {
              return renderSelected(props, smartSpeedName);
            } else if (playbackSpeed === '1.0') {
              return renderSelected(props, 'Normal');
            } else {
              return renderSelected(props, playbackSpeed);
            }
          }}
        >
          Speed
        </Drilldown.Option>
        <Drilldown.Option
          id="videoLabels"
          subPageId="videoLabels"
          renderLabelInfo={(props) => renderSelected(props, capitalizeFirstLetter(displayVideoLabels))}
        >
          Video Labels
        </Drilldown.Option>
      </Drilldown.Page>

      <Drilldown.Page
        id="annotations"
        renderTitle="Markers"
        renderActionLabel={markerBulkUpdateLabel}
        onHeaderActionClicked={async () =>
          await bulkUpdateSelectedAnnotations()
        }
      >
        {renderAnnotationOptionGroup()}
      </Drilldown.Page>

      <Drilldown.Page id="speed" renderTitle="Speed">
        {renderSpeedOptionGroup()}
      </Drilldown.Page>

      <Drilldown.Page id="videoLabels" renderTitle="Video Labels">
        {renderVideoLabelsOptionGroup()}
      </Drilldown.Page>
    </Drilldown>
  );
};

export default AnnotationOptions;
